import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import BorderedButton from "../common/BorderedButton";
import UseTable from "../../components-react/UseTable";
import "../../../styles/wtf-payouts/profit-share.css";
import ButtonDice from "../common/ButtonDice";
import { fetchProfitShareData } from "../../../redux/reducers/apiSlice";
import { updateProfileShareDetails } from "../../../redux/reducers/postApiSlice";
import { toast } from "react-toastify";
import EmptyMessage from "../common/EmptyMessage";
import { selectAuth } from "../../auth/auth.selector";

function ProfitShare() {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectAuth);
  const [decryptedText, setDecryptedText] = useState(userInfo?.user || {});

  const { profitShareData } = useSelector((state) => state.api);
  const selectedGym = useSelector((state) => state?.filters?.selectedGym);
  const selectedGymQueryParam = selectedGym
    ? `&gym_id=${selectedGym?.value}`
    : "";

  const [showEditTable, setShowEditTable] = useState(false);
  const initialProfitShareData = [
    {
      subscription_type: "addon",
      status: "active",
      wtf_share: "",
    },
    {
      subscription_type: "addon_pt",
      status: "active",
      wtf_share: "",
    },
    {
      subscription_type: "nutrition",
      status: "active",
      wtf_share: "",
    },
  ];
  const [allProfitShareData, setAllProfitShareData] = useState(
    initialProfitShareData
  );
  const [buttonLabel, setButtonLabel] = useState("Add");

  const [subscriptionFormValuesArray, setSubscriptionFormValuesArray] =
    useState([]);

  const handleInputChange = (rowIndex, field, value) => {
    const updatedValues = [...subscriptionFormValuesArray];
    updatedValues[rowIndex][field] = value;
    setSubscriptionFormValuesArray(updatedValues);
  };

  const onSave = (e) => {
    e.preventDefault();

    const shareDetails = subscriptionFormValuesArray.map((input, index) => ({
      subscription_type: input?.subscriptionType,
      subscription_name: input?.subscriptionType
        .replace(/_/g, " ")
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
          index === 0 ? word.toUpperCase() : word.toLowerCase()
        )
        .replace(/\s+/g, ""),
      status: input?.subscriptionStatus?.value,
      wtf_share: input?.sharePercentage,
      gym_id: selectedGym?.value,
    }));

    const payload = JSON.stringify({
      share_table: shareDetails,
      gym_id: selectedGym?.value,
    });

    handleUpdateProfitShare(payload, decryptedText?.token);
  };

  const handleUpdateProfitShare = async (formData, authToken) => {
    try {
      const result = await dispatch(
        updateProfileShareDetails({ formData, authToken })
      );

      if (updateProfileShareDetails.fulfilled.match(result)) {
        const successMessage = result.payload.message;
        toast.success(successMessage);
        dispatch(
          fetchProfitShareData({
            queryParameters: selectedGymQueryParam,
            token: decryptedText?.token,
          })
        );
        setShowEditTable(false);
      } else {
        const errorMessage = result.error.message;
        toast.error(errorMessage);
      }
    } catch (error) {
      console.log("handleUpdateProfitShare ERROR => ", error);
    }
  };

  useEffect(() => {
    const initialValues = allProfitShareData.map((rowData) => ({
      subscriptionType: rowData?.subscription_type,
      subscriptionStatus: "active",
      sharePercentage: rowData?.wtf_share,
    }));

    setSubscriptionFormValuesArray(initialValues);
  }, [allProfitShareData]);

  useEffect(() => {
    dispatch(
      fetchProfitShareData({
        queryParameters: selectedGymQueryParam,
        token: decryptedText?.token,
      })
    );
  }, [selectedGym]);

  useEffect(() => {
    if (profitShareData?.status && profitShareData?.data?.length > 0) {
      setAllProfitShareData(profitShareData.data);
      setButtonLabel("Edit");
    } else {
      setAllProfitShareData(initialProfitShareData);
      setButtonLabel("Add");
    }
  }, [profitShareData]);

  const columns = [
    {
      Header: "Subscription type",
      accessor: "subscription_type",
      Cell: ({ value }) => {
        if (value) {
          const formattedValue = value.replace(/_/g, " ").toUpperCase();
          return <span className="tdBolder">{formattedValue}</span>;
        } else {
          return <span className="tdBolder">----</span>;
        }
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => {
        return (
          <span
            className="tdBolder borderRadius"
            style={{
              backgroundColor: value === "active" ? "#D0F6D9" : "#CD0909",
              color: value === "active" ? "#3A974C" : "#FFFFFF",
            }}
          >
            {value || "----"}
          </span>
        );
      },
    },
    {
      Header: "WTF Share %",
      accessor: "wtf_share",
      Cell: ({ value }) => {
        return <span className="tdNormal">₹{value || "----"}</span>;
      },
    },
  ];

  useEffect(() => {
    if (userInfo?.isAuthenticated && userInfo?.user) {
      setDecryptedText(userInfo.user);
    }
  }, [userInfo]);

  return (
    <Container fluid className="container">
      {!selectedGym && (
        <Row>
          <Col>
            <EmptyMessage />
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <div className="createNewSettlementBtn">
            {showEditTable ? (
              <ButtonDice
                buttonText1="Cancel"
                buttonText2="Save"
                onClickButton1={() => setShowEditTable(false)}
                onClickButton2={onSave}
              />
            ) : (
              <>
                {selectedGym && (
                  <BorderedButton
                    buttonText={buttonLabel}
                    onClickButton={() => setShowEditTable(true)}
                  />
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          {showEditTable ? (
            <div className="editTableContainer">
              <div className="editTableHeadContainer">
                {columns?.map((column) => (
                  <span className="editTableHead" key={column.Header}>
                    {column.Header}
                  </span>
                ))}
              </div>

              <div className="editTableBodyContainer">
                {allProfitShareData?.map((rowData, rowIndex) => (
                  <div className="editTableBody" key={rowIndex}>
                    {columns?.map((column) => (
                      <div
                        className="editTableDataContainer"
                        key={column.Header}
                      >
                        {column.accessor === "subscription_type" ? (
                          <span className="editTableData">
                            {rowData?.subscription_type
                              ?.replace(/_/g, " ")
                              .toUpperCase()}
                          </span>
                        ) : (
                          <div className="editTableInput">
                            {column.accessor === "status" ? (
                              <input
                                type="text"
                                className="formInput"
                                value={
                                  subscriptionFormValuesArray[rowIndex]
                                    ?.subscriptionStatus
                                }
                                disabled
                              />
                            ) : (
                              <input
                                type="number"
                                placeholder="Enter Share Percentage"
                                className="formInput"
                                value={
                                  subscriptionFormValuesArray[rowIndex]
                                    ?.sharePercentage
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    rowIndex,
                                    "sharePercentage",
                                    e.target.value
                                  )
                                }
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <UseTable
              columns={columns}
              data={allProfitShareData}
              name="profit-share"
              classame="profit-share-table"
            />
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default ProfitShare;
